import { CreateSimulationGroupRequest, SimulationGroup, Simulation } from '@thive/fddm-types';

import { logger } from '@/modules/common/logging/logger';

import { DeepPartial } from '../useSimulationGroupStore';
import { ISimulationService } from './ISimulationService';

export class SimulationServiceExceptionDecorator implements ISimulationService {
  private decoree: ISimulationService;

  constructor(decoree: ISimulationService) {
    this.decoree = decoree;
  }

  useDraft(): CreateSimulationGroupRequest {
    try {
      return this.decoree.useDraft();
    } catch (err) {
      this.logError('useDraft', err);
      throw err;
    }
  }

  getDraft(): CreateSimulationGroupRequest {
    try {
      return this.decoree.getDraft();
    } catch (err) {
      this.logError('getDraft', err);
      throw err;
    }
  }

  createDraft(name: string): CreateSimulationGroupRequest {
    try {
      return this.decoree.createDraft(name);
    } catch (err) {
      this.logError('createDraft', err);
      throw err;
    }
  }

  createDraftFromRecord(record: SimulationGroup, name: string): CreateSimulationGroupRequest {
    try {
      return this.decoree.createDraftFromRecord(record, name);
    } catch (err) {
      this.logError('createDraftFromRecord', err);
      throw err;
    }
  }

  updateDraft(value: DeepPartial<CreateSimulationGroupRequest>): void {
    try {
      this.decoree.updateDraft(value);
    } catch (err) {
      this.logError('updateDraft', err);
      throw err;
    }
  }

  getRecommendedSimulationTime(): number {
    try {
      return this.decoree.getRecommendedSimulationTime();
    } catch (err) {
      this.logError('getRecommendedSimulationTime', err);
      throw err;
    }
  }

  getMaxCapacity(vehicleType: string): number {
    try {
      return this.decoree.getMaxCapacity(vehicleType);
    } catch (err) {
      this.logError('getMaxCapacity', err);
      throw err;
    }
  }

  getSimulationGroups(): Promise<SimulationGroup[]> {
    try {
      return this.decoree.getSimulationGroups();
    } catch (err) {
      this.logError('getSimulationGroups', err);
      throw err;
    }
  }

  getSimulationGroup(simulationGroupId: string): Promise<SimulationGroup> {
    try {
      return this.decoree.getSimulationGroup(simulationGroupId);
    } catch (err) {
      this.logError('getSimulationGroup', err);
      throw err;
    }
  }

  getSimulationsInGroup(simulationGroupId: string): Promise<Simulation[]> {
    try {
      return this.decoree.getSimulationsInGroup(simulationGroupId);
    } catch (err) {
      this.logError('getSimulationsInGroup', err);
      throw err;
    }
  }

  createSimulationGroup(): Promise<SimulationGroup | null> {
    try {
      return this.decoree.createSimulationGroup();
    } catch (err) {
      this.logError('createSimulationGroup', err);
      throw err;
    }
  }

  reRunSimulationGroup(simulationGroupId: string): Promise<SimulationGroup | null> {
    try {
      return this.decoree.reRunSimulationGroup(simulationGroupId);
    } catch (err) {
      this.logError('reRunSimulationGroup', err);
      throw err;
    }
  }

  renameSimulationGroup(simulationGroupId: string, name: string): Promise<void> {
    try {
      return this.decoree.renameSimulationGroup(simulationGroupId, name);
    } catch (err) {
      this.logError('renameSimulationGroup', err);
      throw err;
    }
  }

  deleteSimulationGroup(simulationGroupId: string): Promise<void> {
    try {
      return this.decoree.deleteSimulationGroup(simulationGroupId);
    } catch (err) {
      this.logError('deleteSimulationGroup', err);
      throw err;
    }
  }

  abortSimulationGroup(simulationGroupId: string): Promise<void> {
    try {
      return this.decoree.abortSimulationGroup(simulationGroupId);
    } catch (err) {
      this.logError('abortSimulationGroup', err);
      throw err;
    }
  }

  downloadSimulationFiles(simulationRunId: string, simulationName: string): Promise<void> {
    try {
      return this.decoree.downloadSimulationFiles(simulationRunId, simulationName);
    } catch (err) {
      this.logError('downloadSimulationFiles', err);
      throw err;
    }
  }

  downloadFmsConfig(): Promise<void> {
    try {
      return this.decoree.downloadFmsConfig();
    } catch (err) {
      this.logError('downloadFmsConfig', err);
      throw err;
    }
  }

  getCustomOrderProfileUrl(): string | undefined {
    try {
      return this.decoree.getCustomOrderProfileUrl();
    } catch (err) {
      this.logError('getCustomOrderProfileUrl', err);
      throw err;
    }
  }

  getCustomFmsConfigUrl(): string | undefined {
    try {
      return this.decoree.getCustomFmsConfigUrl();
    } catch (err) {
      this.logError('getCustomFmsConfigUrl', err);
      throw err;
    }
  }

  getSimulationFiles(): Promise<{
    fmsConfigFiles: object[];
    fmsConfigFileNames: string[];
    orderProfileFiles: Blob[];
    orderProfileFileNames: string[];
  }> {
    try {
      return this.decoree.getSimulationFiles();
    } catch (err) {
      this.logError('getSimulationFiles', err);
      throw err;
    }
  }

  // private utility methods

  private logError(methodName: string, error: unknown) {
    logger.error(`FddmService.${methodName} failed:`, error);
  }
}
